import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFvsIndustryComparison = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('overview');
    
    // Comparison data
    const [comparisonData, setComparisonData] = useState({
        fundBalance: 500000,
        age: 55,
        employmentStatus: 'employed',
        investmentPreference: 'property', // property, shares, diversified
        timeCommitment: 'low', // low, medium, high
        financialKnowledge: 'intermediate', // beginner, intermediate, advanced
        numberOfMembers: 2,
        // SMSF Fees
        smsfAuditFees: 1500,
        smsfAdminFees: 2000,
        smsfAccountingFees: 1800,
        smsfInvestmentFees: 0, // Self-managed
        // Industry Fund Fees (will calculate)
        industryAdminFee: 0,
        industryInvestmentFee: 0
    });

    const [results, setResults] = useState(null);
    const [calculationError, setCalculationError] = useState(null);

    // Calculate comprehensive comparison (runs on button click and once on mount)
    const calculateComparison = () => {
        setCalculationError(null);
        const balance = Number(comparisonData.fundBalance);
        if (!Number.isFinite(balance) || balance <= 0) {
            setCalculationError('Please enter a valid super balance (e.g. 500000).');
            setResults(null);
            setActiveTab('overview');
            return;
        }
        const industryBenchmark = FinancialConfig.INDUSTRY_FUND_BENCHMARKS?.FEES;
        if (!industryBenchmark) {
            setCalculationError('Fee configuration not loaded. Please refresh the page.');
            setResults(null);
            setActiveTab('overview');
            return;
        }
        try {
        // === FEE COMPARISON ===
        const smsfFees = {
            audit: comparisonData.smsfAuditFees,
            admin: comparisonData.smsfAdminFees,
            accounting: comparisonData.smsfAccountingFees,
            investment: comparisonData.smsfInvestmentFees,
            total: comparisonData.smsfAuditFees + comparisonData.smsfAdminFees + 
                   comparisonData.smsfAccountingFees + comparisonData.smsfInvestmentFees
        };
        
        const smsfFeePercent = (smsfFees.total / balance) * 100;

        // Industry fund typical fees (from FinancialConfig) - already validated above
        const industryFees = {
            admin: Math.max(industryBenchmark.ADMIN_FEE_FIXED, balance * industryBenchmark.ADMIN_FEE_PERCENTAGE),
            investment: balance * industryBenchmark.INVESTMENT_FEE_PERCENTAGE,
            total: 0
        };
        industryFees.total = industryFees.admin + industryFees.investment;
        const industryFeePercent = (industryFees.total / balance) * 100;

        const feeSavings = industryFees.total - smsfFees.total;
        const breakEvenBalance = calculateBreakEven(smsfFees.total, industryBenchmark.TYPICAL_TOTAL_FEE);

        // === CONTROL & FLEXIBILITY ===
        const control = {
            smsf: {
                investmentChoice: 10, // 1-10 scale
                assetTypes: ['Property', 'Direct Shares', 'Collectibles', 'Crypto', 'Term Deposits', 'Bonds', 'International'],
                restrictions: 'Sole purpose test, in-house assets 5%, LRBA rules',
                customization: 'Full control over investment strategy'
            },
            industry: {
                investmentChoice: 6,
                assetTypes: ['Managed Funds', 'Limited Direct Shares', 'Term Deposits', 'Balanced Options'],
                restrictions: 'Limited to fund offerings, no direct property',
                customization: 'Choose from preset investment options'
            }
        };

        // === PERFORMANCE POTENTIAL ===
        const performance = {
            smsf: {
                potential: comparisonData.financialKnowledge === 'advanced' ? 'High' : 
                          comparisonData.financialKnowledge === 'intermediate' ? 'Medium' : 'Low',
                dependsOn: 'Your investment skill and time commitment',
                averageReturn: comparisonData.financialKnowledge === 'advanced' ? 8.5 : 7.0,
                risk: 'Fully your responsibility'
            },
            industry: {
                potential: 'Consistent',
                dependsOn: 'Professional fund managers',
                averageReturn: 7.2, // Industry average
                risk: 'Managed by professionals'
            }
        };

        // === TAX EFFICIENCY ===
        const tax = {
            smsf: {
                pensionPhase: '0% tax on earnings',
                frankingCredits: 'Fully utilized',
                capitalGains: 'Strategic tax-loss harvesting available',
                flexibility: 'High - can optimize timing',
                score: 9
            },
            industry: {
                pensionPhase: '0% tax on earnings',
                frankingCredits: 'Utilized but pooled',
                capitalGains: 'Managed by fund',
                flexibility: 'Limited - fund manages',
                score: 7
            }
        };

        // === ESTATE PLANNING ===
        const estate = {
            smsf: {
                nominations: 'Binding death benefit nominations',
                flexibility: 'Full control over beneficiaries',
                reversionary: 'Reversionary pensions available',
                taxPlanning: 'Strategic distributions possible',
                score: 10
            },
            industry: {
                nominations: 'Non-binding or binding (limited)',
                flexibility: 'Trustee has final say',
                reversionary: 'Limited availability',
                taxPlanning: 'Limited options',
                score: 6
            }
        };

        // === INSURANCE ===
        const insurance = {
            smsf: {
                availability: 'External policies only',
                cost: 'Market rates (potentially lower)',
                customization: 'Fully customizable',
                considerations: 'Must arrange separately',
                score: 7
            },
            industry: {
                availability: 'Often included by default',
                cost: 'Group rates (can be competitive)',
                customization: 'Standard packages',
                considerations: 'Automatic coverage',
                score: 8
            }
        };

        // === ADMINISTRATION ===
        const admin = {
            smsf: {
                timeRequired: comparisonData.timeCommitment === 'high' ? '50-100 hours/year' :
                             comparisonData.timeCommitment === 'medium' ? '20-50 hours/year' : 
                             '10-20 hours/year (with advisors)',
                complexity: 'High - annual audits, tax returns, compliance',
                responsibility: 'Trustees are personally responsible',
                penalties: 'Significant for non-compliance',
                score: 4
            },
            industry: {
                timeRequired: '0-5 hours/year',
                complexity: 'Low - managed for you',
                responsibility: 'Fund trustees handle everything',
                penalties: 'Not your concern',
                score: 10
            }
        };

        // === COST OVER TIME ===
        const lifetimeCosts = calculateLifetimeCosts(
            balance,
            smsfFees.total,
            industryFees.total,
            comparisonData.age
        );

        // === OVERALL SCORE ===
        const smsfScore = calculateOverallScore('smsf', {
            fees: feeSavings > 0 ? 8 : 4,
            control: control.smsf.investmentChoice,
            tax: tax.smsf.score,
            estate: estate.smsf.score,
            insurance: insurance.smsf.score,
            admin: admin.smsf.score,
            suitability: calculateSuitability('smsf', comparisonData)
        });

        const industryScore = calculateOverallScore('industry', {
            fees: feeSavings < 0 ? 8 : 4,
            control: control.industry.investmentChoice,
            tax: tax.industry.score,
            estate: estate.industry.score,
            insurance: insurance.industry.score,
            admin: admin.industry.score,
            suitability: calculateSuitability('industry', comparisonData)
        });

        // === RECOMMENDATION ===
        const recommendation = generateRecommendation(
            smsfScore,
            industryScore,
            balance,
            comparisonData
        );

        setResults({
            fees: { smsf: smsfFees, industry: industryFees, savings: feeSavings, breakEven: breakEvenBalance },
            control,
            performance,
            tax,
            estate,
            insurance,
            admin,
            lifetimeCosts,
            scores: { smsf: smsfScore, industry: industryScore },
            recommendation
        });
        setActiveTab('overview');
        } catch (err) {
            console.error('SMSF vs Industry comparison error:', err);
            setCalculationError(err?.message || String(err));
            setResults(null);
            setActiveTab('overview');
        }
    };

    const calculateBreakEven = (fixedCosts, industryFeeRate) => {
        // Fixed SMSF costs / Industry fee rate = break-even balance
        return Math.round(fixedCosts / industryFeeRate);
    };

    const calculateLifetimeCosts = (balance, smsfAnnualFee, industryAnnualFee, age) => {
        const yearsToRetirement = Math.max(0, 67 - age);
        const growthRate = 0.07;
        
        // Project costs over time with growing balance
        let smsfTotal = 0;
        let industryTotal = 0;
        let projectedBalance = balance;
        
        for (let year = 0; year < yearsToRetirement; year++) {
            // SMSF: Fixed annual costs
            smsfTotal += smsfAnnualFee;
            
            // Industry: Percentage-based fees (grow with balance)
            const industryFee = projectedBalance * FinancialConfig.INDUSTRY_FUND_BENCHMARKS.FEES.TYPICAL_TOTAL_FEE;
            industryTotal += industryFee;
            
            // Grow balance
            projectedBalance = projectedBalance * (1 + growthRate);
        }

        return {
            years: yearsToRetirement,
            smsfTotal: Math.round(smsfTotal),
            industryTotal: Math.round(industryTotal),
            difference: Math.round(industryTotal - smsfTotal),
            finalBalance: Math.round(projectedBalance)
        };
    };

    const calculateSuitability = (type, data) => {
        let score = 5;

        if (type === 'smsf') {
            // Balance-based suitability
            if (data.fundBalance >= 500000) score += 2;
            else if (data.fundBalance >= 250000) score += 1;
            else if (data.fundBalance < 200000) score -= 2;

            // Knowledge-based
            if (data.financialKnowledge === 'advanced') score += 2;
            else if (data.financialKnowledge === 'beginner') score -= 2;

            // Time commitment
            if (data.timeCommitment === 'high') score += 1;
            else if (data.timeCommitment === 'low') score -= 1;

            // Investment preference
            if (data.investmentPreference === 'property') score += 2;
        } else {
            // Industry fund suitability (inverse of SMSF)
            if (data.fundBalance < 200000) score += 2;
            else if (data.fundBalance >= 500000) score -= 1;

            if (data.financialKnowledge === 'beginner') score += 2;
            else if (data.financialKnowledge === 'advanced') score -= 1;

            if (data.timeCommitment === 'low') score += 2;
        }

        return Math.max(0, Math.min(10, score));
    };

    const calculateOverallScore = (type, factors) => {
        const weights = {
            fees: 0.20,
            control: 0.15,
            tax: 0.15,
            estate: 0.10,
            insurance: 0.10,
            admin: 0.15,
            suitability: 0.15
        };

        const weighted = Object.entries(factors).reduce((sum, [key, value]) => {
            return sum + (value * (weights[key] || 0));
        }, 0);

        return Math.round(weighted * 10) / 10;
    };

    const generateRecommendation = (smsfScore, industryScore, balance, data) => {
        const diff = smsfScore - industryScore;
        
        if (diff > 1.5) {
            return {
                choice: 'SMSF',
                confidence: 'High',
                reasoning: [
                    balance >= 500000 && 'Your balance ($' + balance.toLocaleString() + ') is well above break-even point',
                    data.investmentPreference === 'property' && 'Direct property investment aligns with SMSF strengths',
                    data.financialKnowledge === 'advanced' && 'Your financial knowledge can maximize SMSF benefits',
                    'Greater control and flexibility for your situation'
                ].filter(Boolean),
                considerations: [
                    'Requires ongoing time commitment',
                    'Personal liability for compliance',
                    'Consider professional advice for setup'
                ]
            };
        } else if (diff < -1.5) {
            return {
                choice: 'Industry Fund',
                confidence: 'High',
                reasoning: [
                    balance < 250000 && 'Lower balance ($' + balance.toLocaleString() + ') favors lower percentage fees',
                    data.timeCommitment === 'low' && 'Industry fund requires minimal time commitment',
                    data.financialKnowledge === 'beginner' && 'Professional management reduces complexity',
                    'Lower administrative burden'
                ].filter(Boolean),
                considerations: [
                    'Limited investment control',
                    'May have higher fees as balance grows',
                    'Can always switch to SMSF later'
                ]
            };
        } else {
            return {
                choice: 'Either Could Work',
                confidence: 'Medium',
                reasoning: [
                    'Both options have merit for your situation',
                    'Decision depends on personal preferences',
                    'Consider your priorities: control vs convenience'
                ],
                considerations: [
                    'Try our detailed comparison below',
                    'Speak to a financial advisor',
                    'Can start with industry fund and transition to SMSF later'
                ]
            };
        }
    };

    // Run comparison once on mount so Overview has initial results
    useEffect(() => {
        calculateComparison();
    }, []);

    const renderOverview = () => {
        if (!results) {
            return (
                <div className="space-y-4">
                    {calculationError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                            <strong>Calculation issue:</strong> {calculationError}
                        </div>
                    )}
                    <div>{calculationError ? 'Fix the issue above and click Calculate Comparison again.' : 'Calculating...'}</div>
                </div>
            );
        }

        const smsfWinner = results.scores.smsf > results.scores.industry;

        return (
            <div className="space-y-6">
                <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    This comparison is for general information and education only. It is not financial or personal advice. Your situation may differ. Seek a licensed financial adviser before making any decision about your super.
                </p>
                {/* Score Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SMSF Card */}
                    <div className={`bg-white p-6 rounded-lg shadow-lg border-t-4 ${
                        smsfWinner ? 'border-green-500' : 'border-gray-300'
                    }`}>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-2xl font-bold text-gray-900">Self-Managed Super</h3>
                            {smsfWinner && <span className="text-2xl">👑</span>}
                        </div>
                        <div className="text-5xl font-bold text-blue-600 mb-2">
                            {results.scores.smsf.toFixed(1)}<span className="text-2xl text-gray-400">/10</span>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Annual Fees:</span>
                                <span className="font-semibold">${results.fees.smsf.total.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Fee %:</span>
                                <span className="font-semibold">{((results.fees.smsf.total / comparisonData.fundBalance) * 100).toFixed(2)}%</span>
                            </div>
                        </div>
                    </div>

                    {/* Industry Fund Card */}
                    <div className={`bg-white p-6 rounded-lg shadow-lg border-t-4 ${
                        !smsfWinner ? 'border-green-500' : 'border-gray-300'
                    }`}>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-2xl font-bold text-gray-900">Industry Fund</h3>
                            {!smsfWinner && <span className="text-2xl">👑</span>}
                        </div>
                        <div className="text-5xl font-bold text-purple-600 mb-2">
                            {results.scores.industry.toFixed(1)}<span className="text-2xl text-gray-400">/10</span>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Annual Fees:</span>
                                <span className="font-semibold">${results.fees.industry.total.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Fee %:</span>
                                <span className="font-semibold">{((results.fees.industry.total / comparisonData.fundBalance) * 100).toFixed(2)}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Neutral note only - no recommendation */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                        SMSF and industry funds have different trade-offs (costs, control, administration). This tool shows a comparison only. It does not recommend any option. For decisions about your super, speak to a licensed financial adviser.
                    </p>
                </div>

                {/* Lifetime Cost Comparison */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">💰 Lifetime Cost Projection</h3>
                    <p className="text-gray-700 mb-4">
                        Over the next {results.lifetimeCosts.years} years until retirement:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-white p-4 rounded">
                            <div className="text-sm text-gray-600">SMSF Total Costs</div>
                            <div className="text-2xl font-bold text-blue-600">
                                ${results.lifetimeCosts.smsfTotal.toLocaleString()}
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded">
                            <div className="text-sm text-gray-600">Industry Fund Total Costs</div>
                            <div className="text-2xl font-bold text-purple-600">
                                ${results.lifetimeCosts.industryTotal.toLocaleString()}
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded">
                            <div className="text-sm text-gray-600">Difference</div>
                            <div className={`text-2xl font-bold ${
                                results.lifetimeCosts.difference > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {results.lifetimeCosts.difference > 0 ? '+' : ''}
                                ${Math.abs(results.lifetimeCosts.difference).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                                {results.lifetimeCosts.difference > 0 ? 'SMSF saves' : 'Industry saves'}
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-gray-600">
                        Note: Assumes {((0.07) * 100).toFixed(0)}% annual growth. Final balance projected: ${results.lifetimeCosts.finalBalance.toLocaleString()}. Illustrative only; not a forecast or advice.
                    </p>
                </div>
            </div>
        );
    };

    const renderDetailed = () => {
        if (!results) return <div>Please calculate comparison first</div>;

        return (
            <div className="space-y-6">
                {/* Fees Breakdown */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">💰 Fee Breakdown</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2">Fee Type</th>
                                    <th className="text-right py-2">SMSF</th>
                                    <th className="text-right py-2">Industry Fund</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-3">Administration</td>
                                    <td className="text-right">${results.fees.smsf.admin.toLocaleString()}</td>
                                    <td className="text-right">${Math.round(results.fees.industry.admin).toLocaleString()}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3">Audit</td>
                                    <td className="text-right">${results.fees.smsf.audit.toLocaleString()}</td>
                                    <td className="text-right text-gray-400">N/A</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3">Accounting</td>
                                    <td className="text-right">${results.fees.smsf.accounting.toLocaleString()}</td>
                                    <td className="text-right text-gray-400">Included</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3">Investment Management</td>
                                    <td className="text-right">${results.fees.smsf.investment.toLocaleString()}</td>
                                    <td className="text-right">${Math.round(results.fees.industry.investment).toLocaleString()}</td>
                                </tr>
                                <tr className="border-t-2 font-semibold">
                                    <td className="py-3">Total Annual Fees</td>
                                    <td className="text-right text-blue-600">${results.fees.smsf.total.toLocaleString()}</td>
                                    <td className="text-right text-purple-600">${Math.round(results.fees.industry.total).toLocaleString()}</td>
                                </tr>
                                <tr className="bg-gray-50">
                                    <td className="py-3">As % of Balance</td>
                                    <td className="text-right">{((results.fees.smsf.total / comparisonData.fundBalance) * 100).toFixed(2)}%</td>
                                    <td className="text-right">{((results.fees.industry.total / comparisonData.fundBalance) * 100).toFixed(2)}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 bg-blue-50 p-4 rounded">
                        <div className="font-semibold mb-1">Break-Even Balance</div>
                        <div className="text-2xl font-bold text-blue-600 mb-1">
                            ${results.fees.breakEven.toLocaleString()}
                        </div>
                        <p className="text-sm text-gray-700">
                            Above this balance, SMSF typically becomes more cost-effective than industry funds
                        </p>
                    </div>
                </div>

                {/* Comparison Matrix */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">📊 Feature Comparison Matrix</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2">
                                    <th className="text-left py-3 px-2">Feature</th>
                                    <th className="text-center py-3 px-2">SMSF</th>
                                    <th className="text-center py-3 px-2">Industry Fund</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-3 px-2 font-medium">Investment Control</td>
                                    <td className="text-center">{renderScore(results.control.smsf.investmentChoice)}</td>
                                    <td className="text-center">{renderScore(results.control.industry.investmentChoice)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3 px-2 font-medium">Tax Efficiency</td>
                                    <td className="text-center">{renderScore(results.tax.smsf.score)}</td>
                                    <td className="text-center">{renderScore(results.tax.industry.score)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3 px-2 font-medium">Estate Planning</td>
                                    <td className="text-center">{renderScore(results.estate.smsf.score)}</td>
                                    <td className="text-center">{renderScore(results.estate.industry.score)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3 px-2 font-medium">Insurance</td>
                                    <td className="text-center">{renderScore(results.insurance.smsf.score)}</td>
                                    <td className="text-center">{renderScore(results.insurance.industry.score)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-3 px-2 font-medium">Ease of Administration</td>
                                    <td className="text-center">{renderScore(results.admin.smsf.score)}</td>
                                    <td className="text-center">{renderScore(results.admin.industry.score)}</td>
                                </tr>
                                <tr className="bg-gray-50 font-semibold border-t-2">
                                    <td className="py-3 px-2">Overall Score</td>
                                    <td className="text-center text-blue-600 text-lg">{results.scores.smsf.toFixed(1)}</td>
                                    <td className="text-center text-purple-600 text-lg">{results.scores.industry.toFixed(1)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Detailed Pros & Cons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SMSF */}
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h4 className="font-semibold text-lg mb-4 text-blue-600">SMSF Pros & Cons</h4>
                        <div className="space-y-4">
                            <div>
                                <div className="font-medium text-green-700 mb-2">✅ Advantages</div>
                                <ul className="text-sm space-y-1 text-gray-700">
                                    <li>• Full investment control</li>
                                    <li>• Direct property investment</li>
                                    <li>• Tax optimization strategies</li>
                                    <li>• Estate planning flexibility</li>
                                    <li>• Lower fees at higher balances</li>
                                    <li>• Consolidated family super</li>
                                </ul>
                            </div>
                            <div>
                                <div className="font-medium text-red-700 mb-2">❌ Disadvantages</div>
                                <ul className="text-sm space-y-1 text-gray-700">
                                    <li>• High setup and ongoing costs</li>
                                    <li>• Time-consuming administration</li>
                                    <li>• Personal liability for compliance</li>
                                    <li>• Requires financial knowledge</li>
                                    <li>• Higher risk if poor decisions</li>
                                    <li>• Annual audit required</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Industry Fund */}
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h4 className="font-semibold text-lg mb-4 text-purple-600">Industry Fund Pros & Cons</h4>
                        <div className="space-y-4">
                            <div>
                                <div className="font-medium text-green-700 mb-2">✅ Advantages</div>
                                <ul className="text-sm space-y-1 text-gray-700">
                                    <li>• Low administration burden</li>
                                    <li>• Professional management</li>
                                    <li>• Lower fees at lower balances</li>
                                    <li>• Automatic insurance included</li>
                                    <li>• No compliance responsibility</li>
                                    <li>• Easy to set up and manage</li>
                                </ul>
                            </div>
                            <div>
                                <div className="font-medium text-red-700 mb-2">❌ Disadvantages</div>
                                <ul className="text-sm space-y-1 text-gray-700">
                                    <li>• Limited investment choice</li>
                                    <li>• No direct property investment</li>
                                    <li>• Less tax optimization</li>
                                    <li>• Estate planning restrictions</li>
                                    <li>• Higher fees at higher balances</li>
                                    <li>• Less control over strategy</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderScore = (score) => {
        const dots = '●'.repeat(score) + '○'.repeat(10 - score);
        return (
            <span className="text-yellow-500 text-lg">{dots}</span>
        );
    };

    const renderInputs = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Enter Your Details</h4>
                <p className="text-sm text-blue-800">
                    Provide your information for a personalized comparison
                </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">Personal Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Super Balance
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={comparisonData.fundBalance}
                            onChange={(e) => setComparisonData({ ...comparisonData, fundBalance: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Your Age
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={comparisonData.age}
                            onChange={(e) => setComparisonData({ ...comparisonData, age: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Members
                        </label>
                        <input
                            {...getNumericInputProps()}
                            min="1"
                            max="6"
                            value={comparisonData.numberOfMembers}
                            onChange={(e) => setComparisonData({ ...comparisonData, numberOfMembers: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Financial Knowledge
                        </label>
                        <select
                            value={comparisonData.financialKnowledge}
                            onChange={(e) => setComparisonData({ ...comparisonData, financialKnowledge: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">Preferences</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Investment Preference
                        </label>
                        <select
                            value={comparisonData.investmentPreference}
                            onChange={(e) => setComparisonData({ ...comparisonData, investmentPreference: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="diversified">Diversified Portfolio</option>
                            <option value="property">Direct Property</option>
                            <option value="shares">Direct Shares</option>
                            <option value="managed">Managed Funds</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time You Can Commit
                        </label>
                        <select
                            value={comparisonData.timeCommitment}
                            onChange={(e) => setComparisonData({ ...comparisonData, timeCommitment: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="low">Low (0-10 hours/year)</option>
                            <option value="medium">Medium (10-30 hours/year)</option>
                            <option value="high">High (30+ hours/year)</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">SMSF Annual Costs (Estimated)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Audit Fees
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={comparisonData.smsfAuditFees}
                            onChange={(e) => setComparisonData({ ...comparisonData, smsfAuditFees: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Admin Fees
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={comparisonData.smsfAdminFees}
                            onChange={(e) => setComparisonData({ ...comparisonData, smsfAdminFees: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Accounting Fees
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={comparisonData.smsfAccountingFees}
                            onChange={(e) => setComparisonData({ ...comparisonData, smsfAccountingFees: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            <button
                type="button"
                onClick={() => {
                    if (typeof console !== 'undefined' && console.log) console.log('[SMSF vs Industry] Calculate Comparison clicked');
                    calculateComparison();
                }}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
            >
                Calculate Comparison
            </button>
        </div>
    );

    const renderDecisionTool = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">🤔 SMSF Suitability Analysis</h3>
                
                <div className="space-y-6">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Consider SMSF if you:</h4>
                        <div className="space-y-2">
                            {[
                                { condition: 'Have at least $200,000-$500,000 in super', recommended: comparisonData.fundBalance >= 200000 },
                                { condition: 'Want to invest in direct property', recommended: comparisonData.investmentPreference === 'property' },
                                { condition: 'Have good financial knowledge', recommended: comparisonData.financialKnowledge !== 'beginner' },
                                { condition: 'Can commit time to administration', recommended: comparisonData.timeCommitment !== 'low' },
                                { condition: 'Want maximum control over investments', recommended: true },
                                { condition: 'Have 2+ family members to include', recommended: comparisonData.numberOfMembers >= 2 }
                            ].map((item, index) => (
                                <div key={index} className="flex items-start">
                                    <span className={`mr-2 ${item.recommended ? 'text-green-500' : 'text-gray-400'}`}>
                                        {item.recommended ? '✓' : '○'}
                                    </span>
                                    <span className="text-sm text-gray-700">{item.condition}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Stick with Industry Fund if you:</h4>
                        <div className="space-y-2">
                            {[
                                { condition: 'Have less than $200,000 in super', recommended: comparisonData.fundBalance < 200000 },
                                { condition: 'Prefer hands-off management', recommended: comparisonData.timeCommitment === 'low' },
                                { condition: 'Want automatic insurance', recommended: true },
                                { condition: 'Limited financial knowledge', recommended: comparisonData.financialKnowledge === 'beginner' },
                                { condition: 'Don\'t want compliance responsibility', recommended: true },
                                { condition: 'Happy with preset investment options', recommended: comparisonData.investmentPreference === 'managed' }
                            ].map((item, index) => (
                                <div key={index} className="flex items-start">
                                    <span className={`mr-2 ${item.recommended ? 'text-green-500' : 'text-gray-400'}`}>
                                        {item.recommended ? '✓' : '○'}
                                    </span>
                                    <span className="text-sm text-gray-700">{item.condition}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* When to Switch */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">🔄 When to Consider Switching</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-medium text-yellow-900 mb-2">Industry → SMSF</h4>
                        <ul className="text-sm space-y-1 text-yellow-800">
                            <li>• Balance exceeds $500,000</li>
                            <li>• Want to buy investment property</li>
                            <li>• Gained financial knowledge/experience</li>
                            <li>• Want more control</li>
                            <li>• Can commit time to management</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-medium text-yellow-900 mb-2">SMSF → Industry</h4>
                        <ul className="text-sm space-y-1 text-yellow-800">
                            <li>• Balance fallen below $200,000</li>
                            <li>• Can't commit required time</li>
                            <li>• Compliance burden too high</li>
                            <li>• Simpler life stage approaching</li>
                            <li>• Want professional management</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">SMSF vs Industry Fund Comparison</h2>
                <p className="text-gray-600">
                    Compare features and typical costs. For education only; not financial advice. Seek a licensed adviser for your situation.
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'overview', label: 'Overview', icon: '📊' },
                        { id: 'inputs', label: 'Your Details', icon: '✏️' },
                        { id: 'detailed', label: 'Detailed Comparison', icon: '🔍' },
                        { id: 'decision', label: 'Decision Tool', icon: '🤔' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'inputs' && renderInputs()}
                {activeTab === 'detailed' && renderDetailed()}
                {activeTab === 'decision' && renderDecisionTool()}
            </div>
        </div>
    );
};

SMSFvsIndustryComparison.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFvsIndustryComparison;

